import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCamera, 
  FaLaptop, 
  FaMobileAlt, 
  FaShieldAlt, 
  FaRocket, 
  FaCheck, 
  FaPhone, 
  FaEnvelope,
  FaInstagram,
  FaFacebookF,
  FaTwitter
} from 'react-icons/fa';
import { BsGraphUp, BsCreditCard2Front, BsLightningCharge, BsGlobe } from 'react-icons/bs';
import { MdPhotoLibrary, MdCloudUpload, MdOutlineDesignServices } from 'react-icons/md';
import { RiCustomerServiceFill } from 'react-icons/ri';

const PhotographerSiteLanding = () => {
  // Form state
  const [formData, setFormData] = useState({
    photographerName: '',
    contactName: '',
    email: '',
    phone: '',
    businessType: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    submitting: false,
    error: false,
    message: ''
  });

  // Modal state
  const [showDemoModal, setShowDemoModal] = useState(false);

  // Show demo modal after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDemoModal(true);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ 
      submitted: false, 
      submitting: true,
      error: false,
      message: ''
    });
    
    // Simulate form submission
    setTimeout(() => {
      setFormStatus({ 
        submitted: true, 
        submitting: false,
        error: false,
        message: 'Thank you! Our team will contact you shortly to discuss your photography website.'
      });
      // Reset form
      setFormData({
        photographerName: '',
        contactName: '',
        email: '',
        phone: '',
        businessType: '',
        message: '',
      });
    }, 1500);
  };

  // Business types options
  const businessTypes = [
    { value: '', label: 'Select Photography Type' },
    { value: 'portrait', label: 'Portrait Photography' },
    { value: 'wedding', label: 'Wedding Photography' },
    { value: 'commercial', label: 'Commercial Photography' },
    { value: 'fashion', label: 'Fashion Photography' },
    { value: 'travel', label: 'Travel Photography' },
    { value: 'wildlife', label: 'Wildlife Photography' },
    { value: 'real_estate', label: 'Real Estate Photography' },
    { value: 'multi', label: 'Multiple Specializations' }
  ];

  // Feature list
  const features = [
    { 
      icon: <MdPhotoLibrary />, 
      title: "Portfolio Galleries", 
      description: "Showcase your best work with responsive, interactive galleries designed to highlight your photography style."
    },
    { 
      icon: <FaCamera />, 
      title: "Booking System", 
      description: "Allow clients to schedule sessions, check your availability, and book your services in just a few clicks."
    },
    { 
      icon: <MdCloudUpload />, 
      title: "Client Galleries", 
      description: "Private, password-protected galleries where clients can view, download and purchase their photos."
    },
    { 
      icon: <BsGlobe />, 
      title: "SEO Optimization", 
      description: "Get found by potential clients with photography-specific SEO designed to rank in local and niche searches."
    },
    { 
      icon: <MdOutlineDesignServices />, 
      title: "Custom Branding", 
      description: "Your website will reflect your unique style and vision with custom colors, fonts, and design elements."
    },
    { 
      icon: <FaMobileAlt />, 
      title: "Mobile Optimization", 
      description: "Fully responsive design ensures your images look stunning on any device, from phones to large desktop monitors."
    }
  ];

  // Testimonials
  const testimonials = [
    {
      name: "Jessica Williams",
      position: "Wedding Photographer",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&h=150",
      quote: "My photography website has never looked better. The client galleries feature has transformed how I deliver photos, and my bookings have increased by 40% since launch!"
    },
    {
      name: "Michael Chen",
      position: "Portrait & Fashion Photographer",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150",
      quote: "The SEO optimization was a game changer. I'm now ranking on the first page for my local area, and the mobile-friendly galleries make my work look stunning on any device."
    },
    {
      name: "Sophia Rodriguez",
      position: "Travel Photographer",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=150&h=150",
      quote: "As someone who's always on the move, having a website that I can easily update from anywhere has been invaluable. The integrated Instagram feed is perfect for my travel work."
    }
  ];

  // Portfolio examples
  const portfolioExamples = [
    {
      title: "Dark Elegance",
      image: "https://images.unsplash.com/photo-1543104216-6921cab14522?auto=format&fit=crop&w=600&h=400",
      type: "Fashion Photography"
    },
    {
      title: "Timeless Moments",
      image: "https://images.unsplash.com/photo-1537907510278-f825d4a4a008?auto=format&fit=crop&w=600&h=400",
      type: "Wedding Photography"
    },
    {
      title: "Urban Explorer",
      image: "https://images.unsplash.com/photo-1533142075521-ee82a7f8a5f1?auto=format&fit=crop&w=600&h=400",
      type: "Portrait Photography"
    },
    {
      title: "Nature's Wonder",
      image: "https://images.unsplash.com/photo-1516298773066-c48f8e9bd92b?auto=format&fit=crop&w=600&h=400",
      type: "Landscape Photography"
    }
  ];

  // Plans/pricing
  const plans = [
    {
      name: "Portfolio",
      price: "₹29,999",
      description: "Perfect for photographers just starting their online presence",
      features: [
        "Responsive website design",
        "5 gallery pages",
        "Contact form",
        "Mobile-friendly design",
        "Social media integration",
        "3 months free maintenance"
      ],
      cta: "Get Started"
    },
    {
      name: "Professional",
      price: "₹59,999",
      description: "For established photographers needing client management",
      features: [
        "Everything in Portfolio",
        "Client galleries",
        "Booking system",
        "Password protection",
        "Blog/stories section",
        "Basic SEO optimization",
        "6 months free maintenance"
      ],
      cta: "Most Popular",
      highlighted: true
    },
    {
      name: "Studio",
      price: "₹99,999",
      description: "Complete solution for professional photography businesses",
      features: [
        "Everything in Professional",
        "E-commerce integration",
        "Print sales capability",
        "Advanced booking with payments",
        "Custom client proofing",
        "Email marketing integration",
        "1 year free maintenance"
      ],
      cta: "Contact Us"
    }
  ];

  return (
    <div className="bg-cyber-dark overflow-hidden">
      {/* Hero Section */}
      <section className="min-h-screen relative flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="w-full h-full bg-[linear-gradient(90deg,rgba(157,78,221,0.03)_1px,transparent_1px),linear-gradient(0deg,rgba(157,78,221,0.03)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
          <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-cyber-dark to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-cyber-dark to-transparent"></div>
          
          {/* Animated particles */}
          {[...Array(20)].map((_, i) => (
            <div 
              key={i} 
              className="absolute bg-cyber-pink rounded-full opacity-20 animate-float"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`
              }}
            ></div>
          ))}
        </div>

        <div className="container mx-auto px-6 pt-32 pb-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl font-orbitron font-bold mb-6">
                <span className="text-white">Stunning </span>
                <span className="text-cyber-pink">Photography Websites</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Showcase your visual masterpieces with a cutting-edge website designed 
                specifically for photographers. Stand out from the competition with beautiful 
                galleries, client management, and booking systems.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => setShowDemoModal(true)}
                  className="cyber-button"
                >
                  <span>Request Free Demo</span>
                </button>
                <a 
                  href="#plans" 
                  className="cyber-button-outline"
                >
                  <span>View Pricing</span>
                </a>
              </div>
              
              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <span className="px-3 py-1 bg-cyber-pink/20 border border-cyber-pink rounded-full text-sm text-cyber-pink font-medium">
                  Limited Time Offer
                </span>
                <p className="text-cyber-neon text-sm">Get 15% off any package when you sign up before June 30!</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="cyber-card p-1 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1542038784456-1ea8e935640e?auto=format&fit=crop&w=1000&q=80" 
                  alt="Photographer portfolio website" 
                  className="w-full h-auto rounded"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-cyber-dark/80 via-transparent to-transparent rounded"></div>
                
                {/* Floating badges */}
                <div className="absolute top-4 left-4 px-4 py-2 bg-cyber-dark/80 border border-cyber-pink rounded-full text-white flex items-center gap-2">
                  <FaCamera className="text-cyber-pink" />
                  <span>Portfolio Showcase</span>
                </div>
                
                <div className="absolute bottom-4 right-4 px-4 py-2 bg-cyber-dark/80 border border-cyber-neon rounded-full text-white flex items-center gap-2">
                  <FaRocket className="text-cyber-neon" />
                  <span>Grow Your Business</span>
                </div>
              </div>
              
              {/* Stats floating */}
              <div className="absolute -bottom-10 -left-10 cyber-card p-4 flex flex-col items-center justify-center w-32 h-32">
                <span className="text-3xl font-bold text-cyber-neon">250+</span>
                <span className="text-xs text-center text-gray-300">Photographer Websites</span>
              </div>
              
              <div className="absolute -top-5 -right-5 cyber-card p-4 flex flex-col items-center justify-center w-28 h-28">
                <span className="text-3xl font-bold text-cyber-pink">45%</span>
                <span className="text-xs text-center text-gray-300">Booking Increase</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action Banner */}
      <section className="py-8 bg-gradient-to-r from-cyber-pink/20 to-cyber-purple/20 border-y border-cyber-pink/30">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-orbitron font-bold text-white">Ready to elevate your photography business?</h2>
              <p className="text-gray-300">Take the first step towards a stunning online presence today.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="#plans" className="cyber-button">
                <span>View Our Packages</span>
              </a>
              <button 
                onClick={() => setShowDemoModal(true)}
                className="cyber-button-outline"
              >
                <span>Get Free Consultation</span>
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              <span className="text-cyber-neon">Features</span> For Photographers
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our websites are designed specifically for photographers with features that showcase your work and streamline your business.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="cyber-card p-6 hover:shadow-cyber-pink/20 hover:shadow-lg transition-all duration-300"
              >
                <div className="bg-cyber-pink/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 text-cyber-pink text-2xl">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-orbitron font-bold mb-3 text-white">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <a href="#contact" className="cyber-button">
              <span>Get These Features</span>
            </a>
          </motion.div>
        </div>
      </section>
      
      {/* Portfolio Examples */}
      <section className="py-20 bg-cyber-gray/10 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyber-pink/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyber-neon/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              Our <span className="text-cyber-pink">Portfolio</span> Showcase
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              See how we've helped photographers present their work with stunning website designs.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {portfolioExamples.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden cyber-card p-1"
              >
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="w-full h-80 object-cover rounded transition-all duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyber-dark to-transparent opacity-80"></div>
                <div className="absolute bottom-0 left-0 p-6">
                  <h3 className="text-xl font-orbitron font-bold text-white">{item.title}</h3>
                  <p className="text-cyber-pink">{item.type}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <button 
              onClick={() => setShowDemoModal(true)}
              className="cyber-button"
            >
              <span>Get Your Custom Design</span>
            </button>
          </motion.div>
        </div>
      </section>
      
      {/* Why Choose Us */}
      <section className="py-20 bg-cyber-dark relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              Why <span className="text-cyber-pink">Choose</span> Us
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We understand the unique digital needs of photographers and visual artists.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="bg-cyber-neon/20 p-2 rounded-lg mr-4">
                    <FaLaptop className="text-cyber-neon text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Visual-First Design</h3>
                    <p className="text-gray-300">
                      Websites that prioritize your images with large, high-quality displays and minimal distractions.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyber-pink/20 p-2 rounded-lg mr-4">
                    <FaShieldAlt className="text-cyber-pink text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Watermarking & Protection</h3>
                    <p className="text-gray-300">
                      Built-in image protection features to prevent theft while still beautifully showcasing your work.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyber-purple/20 p-2 rounded-lg mr-4">
                    <RiCustomerServiceFill className="text-cyber-purple text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Photographer Support</h3>
                    <p className="text-gray-300">
                      Our team understands photography and provides specialized support for your unique business needs.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyber-neon/20 p-2 rounded-lg mr-4">
                    <BsCreditCard2Front className="text-cyber-neon text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Affordable Packages</h3>
                    <p className="text-gray-300">
                      Flexible pricing designed specifically for photography businesses at any stage of growth.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="cyber-card p-1">
                <img 
                  src="https://images.unsplash.com/photo-1552168324-d612d77725e3?auto=format&fit=crop&w=800&h=600&q=80" 
                  alt="Photographer using website on laptop" 
                  className="w-full h-auto rounded"
                />
              </div>
              
              {/* Stats overlay */}
              <div className="absolute -bottom-5 -right-5 bg-cyber-dark border border-cyber-neon p-6 rounded-lg shadow-lg">
                <div className="text-center">
                  <div className="flex justify-between gap-8">
                    <div>
                      <p className="text-3xl font-bold text-cyber-neon">98%</p>
                      <p className="text-sm text-gray-400">Client Satisfaction</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-cyber-pink">3x</p>
                      <p className="text-sm text-gray-400">Inquiry Increase</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-cyber-gray/10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              What <span className="text-cyber-pink">Photographers</span> Say
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Hear from photographers who have transformed their online presence with our websites.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="cyber-card p-6 hover:shadow-cyber-neon/20 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name} 
                    className="w-16 h-16 rounded-full border-2 border-cyber-pink mr-4"
                  />
                  <div>
                    <h4 className="text-lg font-orbitron font-bold text-white">{testimonial.name}</h4>
                    <p className="text-sm text-cyber-neon">{testimonial.position}</p>
                  </div>
                </div>
                <p className="text-gray-300">"{testimonial.quote}"</p>
                <div className="mt-4 flex">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-cyber-pink" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <button 
              onClick={() => setShowDemoModal(true)}
              className="cyber-button"
            >
              <span>Join These Photographers</span>
            </button>
          </motion.div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <section id="plans" className="py-20 bg-cyber-dark relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyber-pink/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyber-neon/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              <span className="text-cyber-neon">Pricing</span> Plans
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Choose the perfect package for your photography business.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`cyber-card p-6 flex flex-col h-full relative ${plan.highlighted ? 'border-cyber-pink' : ''}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-cyber-pink rounded-full text-white text-sm font-medium">
                    Most Popular
                  </div>
                )}
                
                <h3 className="text-2xl font-orbitron font-bold mb-2 text-white">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-3xl font-bold text-cyber-neon">{plan.price}</span>
                  <span className="text-gray-400 ml-1">one-time</span>
                </div>
                <p className="text-gray-300 mb-6">{plan.description}</p>
                
                <div className="flex-grow mb-8">
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <FaCheck className="text-cyber-pink mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button 
                  onClick={() => setShowDemoModal(true)} 
                  className={`${plan.highlighted ? 'cyber-button' : 'cyber-button-outline'} text-center`}
                >
                  <span>{plan.cta}</span>
                </button>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-gray-400 mb-4">Need a custom solution? Contact us for a tailored quote.</p>
            <button 
              onClick={() => setShowDemoModal(true)} 
              className="text-cyber-pink hover:underline"
            >
              Request Custom Package →
            </button>
          </motion.div>
        </div>
      </section>
      
      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-cyber-gray/10 relative">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              Get <span className="text-cyber-pink">Started</span> Today
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Fill out the form below and our team will contact you within 24 hours to discuss your photography website needs.
            </p>
          </motion.div>
          
          <div className="max-w-4xl mx-auto">
            <div className="cyber-card p-8">
              {formStatus.submitted ? (
                <div className="text-center py-8">
                  <div className="bg-cyber-neon/20 border border-cyber-neon text-white p-6 rounded mb-6 inline-block">
                    <svg className="w-16 h-16 mx-auto text-cyber-neon mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <p className="text-xl">{formStatus.message}</p>
                  </div>
                  <button 
                    onClick={() => setFormStatus({submitted: false, submitting: false, error: false, message: ''})}
                    className="cyber-button"
                  >
                    <span>Submit Another Request</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="photographerName" className="block text-white mb-2">Business Name <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="text" 
                        id="photographerName"
                        name="photographerName" 
                        value={formData.photographerName}
                        onChange={handleChange}
                        required 
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Your photography business name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="contactName" className="block text-white mb-2">Your Name <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="text" 
                        id="contactName"
                        name="contactName" 
                        value={formData.contactName}
                        onChange={handleChange}
                        required 
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="email" className="block text-white mb-2">Email Address <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="email" 
                        id="email"
                        name="email" 
                        value={formData.email}
                        onChange={handleChange}
                        required 
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your email"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-white mb-2">Phone Number <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="tel" 
                        id="phone"
                        name="phone" 
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="businessType" className="block text-white mb-2">Photography Type <span className="text-cyber-pink">*</span></label>
                    <select
                      id="businessType"
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                      required
                      className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                    >
                      {businessTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-8">
                    <label htmlFor="message" className="block text-white mb-2">Additional Information</label>
                    <textarea 
                      id="message"
                      name="message" 
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                      placeholder="Tell us about your requirements, timeline, and specific features you're interested in..."
                    ></textarea>
                  </div>
                  
                  {formStatus.error && (
                    <div className="bg-cyber-pink/20 border border-cyber-pink text-white p-4 rounded mb-6">
                      <p>{formStatus.message}</p>
                    </div>
                  )}
                  
                  <div className="flex justify-center">
                    <button 
                      type="submit" 
                      className="cyber-button w-full md:w-auto"
                      disabled={formStatus.submitting}
                    >
                      <span>{formStatus.submitting ? 'Sending...' : 'Get Free Consultation'}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-gray-300 mb-4">Prefer to speak with us directly?</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <a href="mailto:ankitmaharshi55@gmail.com" className="text-white hover:text-cyber-neon transition-colors flex items-center gap-2">
                  <FaEnvelope /> ankitmaharshi55@gmail.com
                </a>
                <a href="tel:+919468532536" className="text-white hover:text-cyber-neon transition-colors flex items-center gap-2">
                  <FaPhone /> +91-9468532536
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-cyber-dark border-t border-cyber-purple/30">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-orbitron font-bold mb-6">
              Ready to <span className="text-cyber-pink">Showcase</span> Your Photography?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Take your photography business to the next level with a stunning website that showcases your unique vision and talent.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button 
                onClick={() => setShowDemoModal(true)}
                className="cyber-button"
              >
                <span>Get Started Now</span>
              </button>
              <button 
                onClick={() => setShowDemoModal(true)}
                className="cyber-button-outline"
              >
                <span>Schedule Demo</span>
              </button>
            </div>

            <div className="mt-8 bg-gradient-to-r from-cyber-pink/20 to-cyber-purple/20 py-4 px-6 rounded-lg inline-block">
              <p className="text-cyber-neon font-bold">LIMITED TIME: 15% OFF ALL PACKAGES</p>
              <p className="text-white text-sm">Offer valid until June 30th. Use code PHOTO15 when ordering.</p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-cyber-dark border-t border-cyber-purple/30 pt-8 pb-6">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="mb-6 md:mb-0">
              <h3 className="text-2xl font-orbitron font-bold text-white mb-2">CyberNest</h3>
              <p className="text-gray-400">Creating stunning websites for photographers</p>
            </div>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-cyber-darker flex items-center justify-center text-white hover:bg-cyber-pink transition-colors">
                <FaInstagram />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-cyber-darker flex items-center justify-center text-white hover:bg-cyber-pink transition-colors">
                <FaFacebookF />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-cyber-darker flex items-center justify-center text-white hover:bg-cyber-pink transition-colors">
                <FaTwitter />
              </a>
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-400">© {new Date().getFullYear()} CyberNest Web Solutions. All rights reserved.</p>
            <p className="text-sm text-gray-500 mt-1">Creating next-generation websites for visual storytellers.</p>
            <div className="mt-4">
              <a href="mailto:ankitmaharshi55@gmail.com" className="text-sm text-gray-400 hover:text-cyber-neon mx-2">Contact</a>
              <a href="#" className="text-sm text-gray-400 hover:text-cyber-neon mx-2">Privacy Policy</a>
              <a href="#" className="text-sm text-gray-400 hover:text-cyber-neon mx-2">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Demo Request Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="cyber-card p-6 max-w-lg w-full relative"
          >
            <button 
              onClick={() => setShowDemoModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            
            <h3 className="text-2xl font-orbitron font-bold mb-4 text-white">Ready for a Beautiful Photography Website?</h3>
            <p className="text-gray-300 mb-6">Sign up now and get 15% off any package. Limited time offer for professional photographers!</p>
            
            <form>
              <div className="mb-4">
                <label htmlFor="demoName" className="block text-white mb-2">Your Name <span className="text-cyber-pink">*</span></label>
                <input 
                  type="text" 
                  id="demoName"
                  required 
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="demoEmail" className="block text-white mb-2">Email Address <span className="text-cyber-pink">*</span></label>
                <input 
                  type="email" 
                  id="demoEmail"
                  required 
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="mb-6">
                <label htmlFor="demoPhone" className="block text-white mb-2">Phone Number <span className="text-cyber-pink">*</span></label>
                <input 
                  type="tel" 
                  id="demoPhone"
                  required 
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="flex justify-between">
                <button 
                  type="button"
                  onClick={() => setShowDemoModal(false)} 
                  className="cyber-button-outline"
                >
                  <span>Maybe Later</span>
                </button>
                <button type="submit" className="cyber-button">
                  <span>Get 15% Off</span>
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PhotographerSiteLanding; 